// User Context for sharing user details across the app
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  auth,
  signInWithGoogle,
  signInWithApple,
  sendPasswordReset,
} from '../firebase';
import { getUserDetails, registerUser, getLanguage, setLanguage as apiSetLanguage, getSubscriptionDetails, getTierLimits, applyPromoCode, sendWelcomeEmail, getCommodities } from '../utils/api';
import {
  trackLoginAttempt,
  trackLoginSuccess,
  trackLoginError,
  trackSignUpAttempt,
  trackSignUpSuccess,
  trackSignUpError,
  trackLogout,
  setAnalyticsUserId,
  useUserAnalytics,
} from '../utils/analytics';
import i18n, { supportedLanguageCodes } from '../i18n';
import { parseLocalePath } from '../constants/Routes';
import { initializeRevenueCat, resetRevenueCat } from '../services/revenuecatService';
import { TIER_FEATURES, isTrialTier } from '../constants/TierFeatures';
import { useTheme } from './ThemeContext';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const { theme, setTheme } = useTheme();
  const queryClient = useQueryClient();

  // Capture the URL-based locale AT MOUNT. If the user landed on
  // `/es/login` (or `/en/...`) we want that to win over whatever stale
  // value Firestore holds for legacy users. The ref survives the post-
  // login navigation away from the auth pages, so the effects below can
  // still reconcile even though window.location.pathname has changed by
  // then. Cleared once reconciliation completes so subsequent language
  // changes from LanguageSelector still propagate normally.
  const urlLocaleRef = useRef(parseLocalePath(window.location.pathname).locale);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);

      if (user) {
        // Start the commodity list here rather than letting the first page
        // that needs it ask. It is the same global list for every user, it
        // only needs a token — which exists by now — and it is the slowest
        // thing the Global Produce Map waits on.
        //
        // Firing it at this point buys two things. Protected routes do not
        // render until `authChecked` below, which is held back by the
        // RevenueCat init on the next line, so this overlaps the request with
        // that wait instead of queueing behind it. And on any navigation that
        // reaches the map from elsewhere, the list is already cached and the
        // dropdown opens instantly.
        //
        // prefetchQuery, not fetchQuery: it shares the ['commodities'] key
        // with useCommodities, resolves into the same cache entry, and never
        // throws — a failure here just leaves the normal query to try again.
        queryClient.prefetchQuery({
          queryKey: ['commodities'],
          queryFn: () => getCommodities(),
          staleTime: 5 * 60 * 1000,
        });

        try {
          await initializeRevenueCat(user.email);
        } catch (error) {
          console.error('Failed to initialize RevenueCat:', error);
        }
      } else {
        resetRevenueCat();
      }

      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, []);

  // Fetch user details from backend when authenticated
  const {
    data: userDetails,
    isLoading: userDetailsLoading,
    isFetching: userDetailsFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['userDetails', firebaseUser?.email],
    queryFn: () => getUserDetails(firebaseUser.email),
    enabled: !!firebaseUser?.email, // Only run when we have an email
    staleTime: 1000 * 60 * 60, // 1 hour
    cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    retry: 2,
  });

  // Fetch subscription details from backend
  const {
    data: subscriptionDetails,
    isLoading: subscriptionLoading,
    refetch: refetchSubscription
  } = useQuery({
    queryKey: ['subscription', firebaseUser?.email],
    queryFn: () => getSubscriptionDetails(firebaseUser.email),
    enabled: !!firebaseUser?.email,
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  // Fetch tier limits (global, doesn't depend on user)
  const { data: globalTierLimitsData, isLoading: tierLimitsLoading } = useQuery({
    queryKey: ['globalTierLimits'],
    queryFn: getTierLimits,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    cacheTime: 1000 * 60 * 60 * 24, // 24 hours
  });

  const isTrialExpired = () => {
    const currentTier = subscriptionDetails?.tier || userDetails?.tier;
    if (!currentTier || !isTrialTier(currentTier)) {
      return false;
    }

    const expiresDate = subscriptionDetails?.expires_date;
    if (!expiresDate) return false;

    return new Date(expiresDate) < new Date();
  };

  const effectiveTier = () => {
    const currentTier = subscriptionDetails?.tier || userDetails?.tier || 'free';

    if (isTrialExpired()) {
      return 'free';
    }

    return currentTier;
  };

  const hasFeature = (feature) => {
    const tier = effectiveTier();
    const features = TIER_FEATURES[tier] || TIER_FEATURES.free;
    return features.includes(feature);
  };

  const isPremium = () => {
    const tier = effectiveTier();
    return tier !== 'free' && !isTrialExpired() && subscriptionDetails?.subscription_status === 'active';
  };

  // Fetch user's language preference when authenticated
  const { data: languageData } = useQuery({
    queryKey: ['userLanguage', firebaseUser?.email],
    queryFn: getLanguage,
    enabled: !!firebaseUser?.email,
    staleTime: 1000 * 60 * 60, // 1 hour
    cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    retry: 1,
  });

  // Apply user's language preference when fetched. If the user arrived
  // via a locale-prefixed URL (e.g. /es/login), that URL locale wins over
  // the value stored in Firestore for legacy users — otherwise we would
  // flip the UI back to English immediately after sign-in.
  useEffect(() => {
    if (!languageData?.status || !languageData?.language) return;
    if (urlLocaleRef.current && supportedLanguageCodes.includes(urlLocaleRef.current)) {
      // URL locale takes precedence; skip applying the stored value. The
      // reconcile effect below will write the URL locale to Firestore.
      return;
    }
    if (languageData.language !== i18n.language) {
      i18n.changeLanguage(languageData.language);
    }
  }, [languageData]);

  // Reconcile URL-based locale (captured at mount from /:lang/… auth
  // routes, or from the legacy ?lang= query param) with the language
  // stored in Firestore. Idempotent: no-op when values match. Only runs
  // once languageData is loaded so we have a stable "stored" value to
  // compare against.
  useEffect(() => {
    if (!firebaseUser?.email) return;
    if (!languageData?.status) return;

    // Prefer the path-based locale captured at mount; fall back to the
    // legacy ?lang= query param for in-flight inbound links.
    let urlLang = urlLocaleRef.current;
    if (!urlLang) {
      urlLang = new URLSearchParams(window.location.search).get('lang');
    }
    if (!urlLang || !supportedLanguageCodes.includes(urlLang)) return;
    if (languageData.language === urlLang) {
      // Already in sync — clear the ref so future LanguageSelector
      // changes aren't blocked by the precedence rule above.
      urlLocaleRef.current = null;
      return;
    }

    apiSetLanguage(urlLang)
      .then(() => {
        // Update the react-query cache so the effect doesn't re-fire on
        // the next refetch (stale cache would keep showing the old stored
        // value and re-post the same write on every refocus).
        queryClient.setQueryData(
          ['userLanguage', firebaseUser.email],
          { status: true, language: urlLang },
        );
        if (i18n.language !== urlLang) {
          i18n.changeLanguage(urlLang);
        }
        // Reconciled — release the precedence lock.
        urlLocaleRef.current = null;
      })
      .catch((err) => {
        console.warn('UserContext: failed to reconcile URL locale with stored language', err);
      });
  }, [firebaseUser?.email, languageData, queryClient]);

  // Apply appearance from server once user details are stable (avoid stale cache + toggle races).
  // Default when missing: light (matches new-user default and product preference).
  useEffect(() => {
    if (!firebaseUser?.email) return;
    if (userDetailsFetching) return;
    if (!userDetails) return;

    const desired =
      userDetails.appearance === 'dark_mode' ? 'dark' : 'light';

    if (theme !== desired) {
      setTheme(desired);
    }
  }, [
    firebaseUser?.email,
    userDetails,
    userDetailsFetching,
    userDetails?.appearance,
    theme,
    setTheme,
  ]);

  // Set up user analytics tracking (must be after userDetails is defined)
  useUserAnalytics(userDetails, firebaseUser);

  // Authentication methods
  const signIn = async (email, password) => {
    trackLoginAttempt('email');
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      // Set user ID first, then track success
      setAnalyticsUserId(userCredential.user.uid);
      trackLoginSuccess('email', email);
      return userCredential.user;
    } catch (error) {
      trackLoginError('email', error.code);
      throw error;
    }
  };

  // Signup flow:
  //   1. Create Firebase Auth account (client-side)
  //   2. registerUser() → backend creates Firestore doc & marks email_verified=True
  //   3. applyPromoCode('ELITE30') → 30-day Elite trial   (commented out)
  //   4. sendWelcomeEmail() → fire-and-forget            (commented out)
  //   5. Sign out → redirect to /signup-success (public page)
  //   6. User clicks "Log In" → /login → dashboard
  //
  // Email verification is handled server-side in add_user_db (create_user_db)
  // so the user never needs to verify manually.
  const register = async (email, password, name, surname, language) => {
    trackSignUpAttempt('email');
    setIsRegistering(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Normalize language against the shipped locale list so we never
      // forward a value the backend would reject. Passing it here means
      // the Firestore user doc is created WITH language already set — no
      // window where a fresh Spanish user has no language field. If the
      // initial write is lost for any reason, the reconcile effect above
      // retries on the next load.
      const normalizedLanguage = supportedLanguageCodes.includes(language) ? language : undefined;
      await registerUser(email, password, name, surname, normalizedLanguage);

      // Apply 30-day Elite promotional entitlement
      // try { await applyPromoCode(email, 'ELITE30'); } catch (e) { console.warn('Elite promo failed:', e.message); }

      // Fire-and-forget welcome email
      // sendWelcomeEmail(email, name).catch(() => {});

      setAnalyticsUserId(user.uid);
      trackSignUpSuccess('email', email);

      // Sign out so user lands on /signup-success without triggering
      // authenticated redirects (PublicRoute would redirect to dashboard)
      await firebaseSignOut(auth);

      return user;
    } catch (error) {
      trackSignUpError('email', error.code);
      throw error;
    } finally {
      setIsRegistering(false);
    }
  };

  const signOut = async () => {
    try {
      trackLogout();
      await firebaseSignOut(auth);
    } catch (error) {
      throw error;
    }
  };

  const invalidateUserQueries = async (email) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['userDetails', email] }),
      queryClient.invalidateQueries({ queryKey: ['subscription', email] }),
    ]);
  };

  const signInWithGoogleProvider = async () => {
    trackLoginAttempt('google');
    try {
      const user = await signInWithGoogle();
      // Set user ID first for all subsequent events
      setAnalyticsUserId(user.uid);

      // Check if user exists in backend, if not create them
      try {
        await getUserDetails(user.email);
        trackLoginSuccess('google', user.email);
      } catch (error) {
        // User doesn't exist, create them
        const displayName = user.displayName || '';
        const [firstName = '', ...lastNameParts] = displayName.split(' ');
        const lastName = lastNameParts.join(' ');
        await registerUser(user.email, '', firstName, lastName);
        // The auth state change fired the moment the popup resolved, so the
        // userDetails/subscription queries already ran and 404'd against a
        // record that did not exist yet. Nothing refetches them on its own
        // (staleTime is an hour), which is why a fresh social signup landed in
        // the app with no name and no tier until a hard reload.
        await invalidateUserQueries(user.email);

        // Apply 30-day Elite promotional entitlement
        // try { await applyPromoCode(user.email, 'ELITE30'); } catch (e) { console.warn('Elite promo failed:', e.message); }

        // Fire-and-forget welcome email
        // sendWelcomeEmail(user.email, firstName).catch(() => {});

        trackSignUpSuccess('google', user.email);
      }
      return user;
    } catch (error) {
      trackLoginError('google', error.code);
      throw error;
    }
  };

  const signInWithAppleProvider = async () => {
    trackLoginAttempt('apple');
    try {
      const user = await signInWithApple();
      // Set user ID first for all subsequent events
      setAnalyticsUserId(user.uid);

      // Check if user exists in backend, if not create them
      try {
        await getUserDetails(user.email);
        trackLoginSuccess('apple', user.email);
      } catch (error) {
        // User doesn't exist, create them
        const displayName = user.displayName || '';
        const [firstName = '', ...lastNameParts] = displayName.split(' ');
        const lastName = lastNameParts.join(' ');
        await registerUser(user.email, '', firstName, lastName);
        // The auth state change fired the moment the popup resolved, so the
        // userDetails/subscription queries already ran and 404'd against a
        // record that did not exist yet. Nothing refetches them on its own
        // (staleTime is an hour), which is why a fresh social signup landed in
        // the app with no name and no tier until a hard reload.
        await invalidateUserQueries(user.email);

        // Apply 30-day Elite promotional entitlement
        // try { await applyPromoCode(user.email, 'ELITE30'); } catch (e) { console.warn('Elite promo failed:', e.message); }

        // Fire-and-forget welcome email
        // sendWelcomeEmail(user.email, firstName).catch(() => {});

        trackSignUpSuccess('apple', user.email);
      }
      return user;
    } catch (error) {
      trackLoginError('apple', error.code);
      throw error;
    }
  };

  const sendPasswordResetEmail = async (email) => {
    try {
      await sendPasswordReset(email);
      return true;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    firebaseUser,
    userDetails,
    authChecked,
    isRegistering,
    userDetailsLoading,
    error,
    refetchUserDetails: refetch,
    isAuthenticated: !!firebaseUser,
    tier: effectiveTier(),
    tierLimits: userDetails?.tier_limits || null,
    globalTierLimits: globalTierLimitsData?.tiers || null,
    chartRestrictions: globalTierLimitsData?.chart_restrictions || null,
    email: firebaseUser?.email || null,
    subscriptionDetails,
    subscriptionLoading,
    refetchSubscription,
    hasFeature,
    isPremium,
    isTrialExpired: isTrialExpired(),
    // Auth methods
    signIn,
    register,
    signOut,
    signInWithGoogle: signInWithGoogleProvider,
    signInWithApple: signInWithAppleProvider,
    sendPasswordResetEmail,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
